/**
 * Chats.tsx — Admin all-accounts chats surface (Phase 12-04; full-parity update 2026-07-20).
 *
 * Mounts the shared `@crm/chat` workspace in `allAccountsMode` with `readOnly={false}` so admin
 * operators can do everything a chatter can — send/PPV/free-media, mute, block/unblock/report,
 * delete, mark-unread, fan notes/tags, AND mass-messages — across ALL managed accounts (owner
 * 2026-07-20: "admins need to send messages as well and see the same information chatters see").
 * The passive-on-open side-effects (collision claim + auto-mark-read) stay suppressed via
 * allAccountsMode — a supervising admin scanning many accounts must not stamp "being viewed" or
 * wipe a chatter's unread signal merely by opening a thread (T-12-11); explicit clicks still act.
 *
 * The Broadcasts + Notifications(Transactions) + FanPanel "Recent payments" slots are the SAME
 * shared components the chatter surface uses (relocated into @crm/chat 2026-07-20). Broadcasts +
 * Notifications are per-creator, so they surface on a per-creator tab, not the merged "All" tab.
 *
 * WsProvider note: admin-app's own `lib/WsProvider.tsx` is a separate, admin-local Socket.IO
 * context (used elsewhere for onboarding validation progress + health badges) — NOT the same
 * React context as `@crm/chat`'s internal `useWsClient()`, which `ChatWorkspace` depends on for
 * live `inbox:delta`/`health:update` pushes. Rather than fork/replace that shared admin socket
 * layer, this page nests `@crm/chat`'s own `WsProvider` locally so `ChatWorkspace`'s
 * `useWsClient()` resolves — a second, page-scoped Socket.IO connection, torn down on unmount.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChatWorkspace,
  WsProvider,
  BroadcastsModal,
  TransactionsPane,
  transactionsApi,
} from '@crm/chat';

export function Chats() {
  const queryClient = useQueryClient();

  // Batch transactions unread-counts map (D-07-15). One round-trip covers every creator's tx
  // unread counts — drives the amber badge on each per-creator tab. For an admin the server
  // returns counts across ALL creators (chatter-transactions admin branch).
  const txUnreadQuery = useQuery({
    queryKey: ['admin-tx-unread-counts'],
    queryFn: () => transactionsApi.unreadCounts(),
    staleTime: 60_000,
  });

  return (
    <WsProvider>
      <ChatWorkspace
        allAccountsMode
        readOnly={false}
        basePath="/chats"
        BroadcastsModal={BroadcastsModal}
        TransactionsPane={TransactionsPane}
        txUnreadMap={txUnreadQuery.data}
        onTransactionsViewOpen={(creatorId) => {
          transactionsApi
            .markSeen(creatorId)
            .then(() => queryClient.invalidateQueries({ queryKey: ['admin-tx-unread-counts'] }))
            .catch(() => {
              /* non-fatal: badge may not clear immediately */
            });
        }}
        fetchRecentPayments={(memberId, creatorId) =>
          transactionsApi.chatterFeed(creatorId, undefined, undefined, memberId, 5)
        }
      />
    </WsProvider>
  );
}
